/**
 * Demo-seed version stamp (S1.1) — the zustand `persist` `version` for the
 * pricing store's dev/mock-mode persistence.
 *
 * Lives in src (not mocks/): production src never imports mock modules, and
 * the persist options need this constant at store-module init.
 *
 * BUMP DISCIPLINE — any edit to the seeded `mockPricingDataset`
 * (mocks/data/pricing/index.ts) MUST, in the same change:
 *   1. bump `SEED_VERSION`, and
 *   2. re-record `SEED_HASH` (sha256 hex of `JSON.stringify(mockPricingDataset)`).
 * A persisted demo dataset from an older seed generation is DROPPED at
 * rehydrate (version mismatch → reset-to-seeds path), so stale localStorage
 * can never shadow retuned seeds. The pinned parity test
 * (`src/stores/__tests__/pricingStorePersistence.test.ts`) recomputes the
 * hash — editing seeds without coming through this file goes RED.
 *
 * Scope: `mockPricingDataset` ONLY (that is what the store persists).
 * `mockExchangeRates` is display data outside the dataset and outside this
 * stamp — its dynamic effectiveDate never forces a bump.
 */

/**
 * 4 → 5 (B0, 2026-08-31): the two seeded `markup_pct` special prices gained
 * the required `basisAtAuthoring` provenance field. `specialItemPrices` sits
 * inside `mockPricingDataset`, so the stamp moves. No seeded NUMBER changed —
 * the bump records a shape change, not a retune.
 *
 * 5 → 6 (Option B, 2026-08-31): `mockPricingDataset` gained `productAnchors`,
 * the per-product Pricing Anchor picks the resolver now reads. Still no seeded
 * NUMBER changed — the collection is DERIVED from `Product.pricingAnchor` and
 * adds a field, not a value. The RESOLVED prices did move (that is the flip),
 * but resolved prices are computed, never stored, so they are outside this
 * stamp by construction.
 */
/**
 * 6 → 7 (product-group band scope P1, 2026-08-31): `mockPricingDataset` gained
 * `productBandGroups` (DERIVED from `Product.categoryId`, like the anchor
 * collection), FOUR new `priceGroupBands` rows carrying `productGroupId:
 * 'cat-ele-tl'`, and the 17 `markupRules` cells of the one reachable scoped
 * band × source. The DEFAULT A–D and TWD band rows are byte-identical — no
 * seeded band's bounds were re-cut (RULING R-AD), and the four default AUD
 * rows are still pinned field-for-field by `pricingTwdExemplarSeeds`.
 *
 * Prices DID move, for exactly one product group: prod-tail-lamp-01 now bands
 * on the `cat-ele-tl` set (B [25,60)) instead of the default set (A [0,50)),
 * so at 2026-07-17 C1 72.10 → 80.34 · C2 68.49 → 76.32 · C3 68.80 → 76.22.
 * Resolved prices are computed, never stored, so they sit outside this stamp
 * by construction — recorded here because a demo whose numbers move without a
 * written reason is how a seed retune gets mistaken for a defect.
 */
export const SEED_VERSION = 7;

/** sha256 of JSON.stringify(mockPricingDataset) — re-recorded with each bump. */
export const SEED_HASH = '3294704153f4cf1407d66a4d74923e14ca634e4a7cc644d12fb2d12d4227c770';
